import { instructionStrings } from '@data/instruction';
import { FileTypeHelper, LocalFile, RemoteFile } from '@models/next/FileType';
import { PaginationType } from '@models/next/PaginationType';
import { f } from '@utils/nextForms';
import dayjs from 'dayjs';
import { equals, pick } from 'remeda';
import { z } from 'zod';

export const BlanketInsuranceActionStatus = z.enum(['edit', 'createSuccess', 'editSuccess']);

export type BlanketInsuranceActionStatus = z.infer<typeof BlanketInsuranceActionStatus>;

export const BlanketInsuranceFields = z.object({
  id: z.number(),
  company: z.string().label(instructionStrings.insuranceCompany),
  policy_no: z.string().label(instructionStrings.insurancePolicyNo),
  start: f.dateString().label(instructionStrings.insurancePeriod),
  end: f.dateString().label(instructionStrings.insurancePeriodEnd),
  blanket_insurance_files: z.array(RemoteFile).label(instructionStrings.additionalFiles),
  local_blanket_insurance_files: z.array(LocalFile).label(instructionStrings.additionalFiles),
});

export type BlanketInsuranceFields = z.infer<typeof BlanketInsuranceFields>;

export const BlanketInsuranceSearchQuery = z
  .object({
    ...PaginationType.shape,
    company: BlanketInsuranceFields.shape.company,
    policy_no: BlanketInsuranceFields.shape.policy_no,
    valid_date: f.dateString().label(instructionStrings.insurancePeriod),
    ordering: z.string().array(),
    user_id: z.number(),
  })
  .partial();

export type BlanketInsuranceSearchQuery = z.infer<typeof BlanketInsuranceSearchQuery>;

export const BlanketInsurance = z.object({
  id: BlanketInsuranceFields.shape.id,
  company: BlanketInsuranceFields.shape.company,
  policy_no: BlanketInsuranceFields.shape.policy_no,
  start: BlanketInsuranceFields.shape.start,
  end: BlanketInsuranceFields.shape.end,
  blanket_insurance_files: BlanketInsuranceFields.shape.blanket_insurance_files,
});

export type BlanketInsurance = z.infer<typeof BlanketInsurance>;

export const BlanketInsuranceBase = z.object({
  company: BlanketInsuranceFields.shape.company,
  policy_no: BlanketInsuranceFields.shape.policy_no,
  start: BlanketInsuranceFields.shape.start,
  end: BlanketInsuranceFields.shape.end,
  blanket_insurance_files: BlanketInsuranceFields.shape.blanket_insurance_files,
});

export type BlanketInsuranceBase = z.infer<typeof BlanketInsuranceBase>;

export const BlanketInsuranceForm = z
  .object({
    company: BlanketInsuranceFields.shape.company.min(1),
    policy_no: BlanketInsuranceFields.shape.policy_no.min(1),
    start: BlanketInsuranceFields.shape.start,
    end: BlanketInsuranceFields.shape.end,
    local_blanket_insurance_files: BlanketInsuranceFields.shape.local_blanket_insurance_files,
  })
  .superRefine((data, ctx) => {
    if (data.start && data.end && data.end < data.start) {
      ctx.addIssue({
        code: 'custom',
        path: ['start'],
        message: instructionStrings.errorBlanketInsuranceEndBeforeStart,
      });
    }
  });

export type BlanketInsuranceForm = z.infer<typeof BlanketInsuranceForm>;

export const PostBlanketInsurance = z.object({
  company: BlanketInsuranceFields.shape.company,
  policy_no: BlanketInsuranceFields.shape.policy_no,
  start: BlanketInsuranceFields.shape.start,
  end: BlanketInsuranceFields.shape.end,
  blanket_insurance_files: BlanketInsuranceFields.shape.blanket_insurance_files,
});

export type PostBlanketInsurance = z.infer<typeof PostBlanketInsurance>;

export const PatchBlanketInsurance = PostBlanketInsurance;

export type PatchBlanketInsurance = z.infer<typeof PatchBlanketInsurance>;

export class BlanketInsuranceHelpers {
  static mapToForm(blanketInsurance: BlanketInsurance): BlanketInsuranceForm {
    return {
      ...blanketInsurance,
      local_blanket_insurance_files: blanketInsurance.blanket_insurance_files.map(
        FileTypeHelper.remoteToLocal,
      ),
    };
  }

  static areEqualValue(a: BlanketInsuranceBase, b: BlanketInsuranceBase) {
    const comparedKeys: (keyof BlanketInsuranceBase)[] = [
      'company',
      'policy_no',
      'start',
      'end',
      'blanket_insurance_files',
    ];

    return equals(pick(a, comparedKeys), pick(b, comparedKeys));
  }

  static isExpired(a: BlanketInsuranceBase) {
    return dayjs(a.end).isBefore(dayjs(), 'day');
  }
}
